import { PersonGroupIcon } from '@navikt/aksel-icons';
import { ContextDataType, useContextGetData, useContextSaveData } from 'appData/PlanleggerDataContext';
import { PlanleggerRoutes } from 'appData/routes';
import { usePlanleggerNavigator } from 'appData/usePlanleggerNavigator';
import { useStepData } from 'appData/useStepData';
import { PlanleggerStepPage } from 'components/page/PlanleggerStepPage';
import dayjs from 'dayjs';
import { isEqual } from 'es-toolkit';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { erAlenesøker as erAlene, erFarOgFar, erMedmorDelAvSøknaden } from 'utils/HvemPlanleggerUtils';
import { erBarnetAdoptert, erBarnetFødt } from 'utils/barnetUtils';

import { BodyShort, Heading, Link, Radio, Spacer, VStack } from '@navikt/ds-react';

import { DATE_3_YEARS_AGO, links } from '@navikt/fp-constants';
import { RhfForm, StepButtonsHookForm } from '@navikt/fp-form-hooks';
import { OmBarnetPlanlegger } from '@navikt/fp-types';
import { Infobox } from '@navikt/fp-ui';
import { useScrollBehaviour } from '@navikt/fp-utils';
import { isRequired, notEmpty } from '@navikt/fp-validation';

import { BlueRadioGroup } from '../../components/form-wrappers/BlueRadioGroup';
import { Adopsjon } from './Adopsjon';
import { Fødsel } from './fødsel/Fødsel';

const finnHvorMangeBarnLabel = (erAlenesøker: boolean, erFødsel: boolean) => {
    if (erFødsel) {
        return <FormattedMessage id="OmBarnetSteg.HvorMange" values={{ erAlenesøker }} />;
    }

    return <FormattedMessage id="OmBarnetSteg.Adopsjon.HvorMange" values={{ erAlenesøker }} />;
};

export const OmBarnetPlanleggerSteg = () => {
    const intl = useIntl();
    const navigator = usePlanleggerNavigator();
    const stepConfig = useStepData();

    const omBarnet = useContextGetData(ContextDataType.OM_BARNET);
    const hvemPlanlegger = notEmpty(useContextGetData(ContextDataType.HVEM_PLANLEGGER));
    const oppdaterOmBarnetPlanlegger = useContextSaveData(ContextDataType.OM_BARNET);
    const oppdaterUttaksplan = useContextSaveData(ContextDataType.UTTAKSPLAN);

    const lagre = (formValues: OmBarnetPlanlegger) => {
        oppdaterOmBarnetPlanlegger(formValues);
        if (omBarnet && !isEqual(omBarnet, formValues)) {
            oppdaterUttaksplan(undefined);
        }

        if (erBarnetFødt(formValues) && dayjs(formValues.fødselsdato).isBefore(DATE_3_YEARS_AGO)) {
            navigator.goToNextStep(PlanleggerRoutes.OPPSUMMERING);
        } else if (erBarnetAdoptert(formValues)) {
            navigator.goToNextStep(PlanleggerRoutes.ARBEIDSSITUASJON);
        } else {
            navigator.goToNextStep(PlanleggerRoutes.BARNEHAGEPLASS);
        }
    };

    const formMethods = useForm<OmBarnetPlanlegger>({
        shouldUnregister: true,
        defaultValues: omBarnet,
    });

    const erFødsel = formMethods.watch('erFødsel');
    const antallBarn = formMethods.watch('antallBarn');

    const erAlenesøker = erAlene(hvemPlanlegger);
    const erFedre = erFarOgFar(hvemPlanlegger);
    const erMorOgMedmor = erMedmorDelAvSøknaden(hvemPlanlegger);

    const { ref, scrollToBottom } = useScrollBehaviour();

    return (
        <PlanleggerStepPage ref={ref} steps={stepConfig} goToStep={navigator.goToNextStep}>
            <RhfForm formMethods={formMethods} onSubmit={lagre} shouldUseFlexbox>
                <VStack gap="space-40" style={{ flex: 1 }}>
                    <VStack gap="space-32">
                        <Heading level="2" size="medium">
                            <FormattedMessage id="OmBarnetSteg.Tittel" />
                        </Heading>
                        <BlueRadioGroup
                            name="erFødsel"
                            control={formMethods.control}
                            label={<FormattedMessage id="OmBarnetSteg.HvaGjelder" values={{ erAlenesøker }} />}
                            validate={[
                                isRequired(
                                    intl.formatMessage(
                                        {
                                            id: 'OmBarnetSteg.HvaGjelder.Required',
                                        },
                                        { erAlenesøker },
                                    ),
                                ),
                            ]}
                            onChange={() => {
                                formMethods.resetField('antallBarn');
                                scrollToBottom();
                            }}
                        >
                            <Radio value={true} autoFocus>
                                <FormattedMessage id="OmBarnetSteg.Fødsel" />
                            </Radio>
                            <Radio value={false} disabled={erMorOgMedmor}>
                                <FormattedMessage id="OmBarnetSteg.Adopsjon" />
                            </Radio>
                        </BlueRadioGroup>
                        {erFødsel !== undefined && erFødsel === true && erFedre && (
                            <Infobox
                                header={<FormattedMessage id="OmBarnetSteg.Fødsel.Infoboks" />}
                                icon={
                                    <PersonGroupIcon
                                        height={24}
                                        width={24}
                                        color="var(--ax-bg-success-strong)"
                                        fontSize="1.5rem"
                                        aria-hidden
                                    />
                                }
                                color="green"
                            >
                                <BodyShort>
                                    <FormattedMessage id="OmBarnetSteg.Fødsel.Infoboks.DenSomErBiologiskFar" />
                                </BodyShort>
                                <BodyShort>
                                    <FormattedMessage
                                        id="OmBarnetSteg.Fødsel.Infoboks.LesMer"
                                        values={{
                                            a: (msg) => (
                                                <Link href={links.foreldrepengerFarOgFar} target="_blank" inlineText>
                                                    {msg}
                                                </Link>
                                            ),
                                        }}
                                    />
                                </BodyShort>
                            </Infobox>
                        )}
                        {erFødsel !== undefined && (
                            <BlueRadioGroup
                                name="antallBarn"
                                control={formMethods.control}
                                label={finnHvorMangeBarnLabel(erAlenesøker, erFødsel)}
                                shouldFadeIn
                                validate={[
                                    isRequired(
                                        intl.formatMessage(
                                            {
                                                id: 'OmBarnetSteg.HvorMange.Required',
                                            },
                                            { erAlenesøker, erFødsel },
                                        ),
                                    ),
                                ]}
                                onChange={() => {
                                    formMethods.resetField('erBarnetFødt');
                                    scrollToBottom();
                                }}
                            >
                                <Radio value="1">
                                    <FormattedMessage id="OmBarnetSteg.Ett" />
                                </Radio>
                                <Radio value="2">
                                    {erFødsel ? (
                                        <FormattedMessage id="OmBarnetSteg.Tvillinger" />
                                    ) : (
                                        <FormattedMessage id="OmBarnetSteg.To" />
                                    )}
                                </Radio>
                                <Radio value="3">
                                    <FormattedMessage id="OmBarnetSteg.FlereEnnTo" />
                                </Radio>
                            </BlueRadioGroup>
                        )}
                        {erFødsel && antallBarn && (
                            <Fødsel
                                hvemPlanlegger={hvemPlanlegger}
                                erOmBarnetPlanleggerIkkeOppgittFraFør={omBarnet === undefined}
                                antallBarn={antallBarn}
                                scrollToBottom={scrollToBottom}
                            />
                        )}
                        {erFødsel === false && antallBarn && (
                            <Adopsjon
                                erAlenesøker={erAlenesøker}
                                erOmBarnetPlanleggerIkkeOppgittFraFør={omBarnet === undefined}
                                antallBarn={antallBarn}
                            />
                        )}
                    </VStack>
                    <Spacer />
                    <StepButtonsHookForm<OmBarnetPlanlegger>
                        saveDataOnPreviousClick={oppdaterOmBarnetPlanlegger}
                        goToPreviousStep={navigator.goToPreviousDefaultStep}
                        useSimplifiedTexts
                    />
                </VStack>
            </RhfForm>
        </PlanleggerStepPage>
    );
};
